import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import cn from 'classnames'
import DateFnsUtils from '@date-io/date-fns'
import { getFormattedDate, getDateFormat } from 'helpers/dates'
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers'
import { Collapse, TextField } from '@material-ui/core'
import { ReactComponent as SchedulingIcon } from 'assets/img/triggers-scheduling-icon.svg'
import { ReactComponent as ClockIcon } from 'assets/img/ic_clock.svg'
import SelectInput from 'components/common/Inputs/SelectInput'
import Radio from 'components/common/Radio'
import { DatePickerPaper, TimePickerPaper } from 'components/common/styles'
import { PURPLE } from 'constants/colors'
import ProfileHelper from 'helpers/profile'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import {
  StyledCondition,
  StyledModalBoxSection,
  StyledName,
  StyledRuleSettings,
  StyledTitle,
} from 'components/common/Modals/styles'
import { setFormData } from 'reducers/managementUI/reducer'
import {
  ADDITIONAL_REPEAT_ACTIONS,
  ADDITIONAL_END_DELAY_ACTIONS,
  VALUE_CUSTOM,
  VALUE_DAYS,
  VALUE_MONTHS,
  VALUE_WEEKS,
  FIELD_START_DATE,
  FIELD_DUE_DATE,
  FIELD_DUE_DATE_DATE,
  FIELD_DUE_DATE_TIME,
  VALUE_NEVER,
  REGULAR_REPEAT_SCHEDULE,
  END_DATE,
  END_DATE_AFTER_TERM,
  END_DATE_AFTER_NUMBER,
  REPEAT_BY,
  FREQUENCY,
  REPEAT,
  WEEK_ORDINARY_NUMBER,
  DAY_OF_WEEK,
  DAY_OF_MONTH,
  DAYS_OF_WEEK,
  FORMATTED_STOP_RECURRENCE_ACTIONS,
  DAYS_OF_WEEK_SHORTENED_OPTIONS,
  DAYS_OF_MONTH,
  WEEK_NUMBERS,
  DAYS_OF_WEEK_OPTIONS,
  VALUE_AFTER,
  START_TIME,
  VALUE_ON,
  END_DATE_ON_DATE,
  VALUE_OCCURRENCES,
} from 'constants/managementUI'
import { StyledTimePicker } from 'components/common/Inputs/styles'
import { Licensee } from 'types/common'
import messages from '../../translate'
import { DaysContainer, Day } from '../PlanNewJob.styles'
import {
  DatePickerContainer,
  RadioContainer,
  TextFieldContainer,
} from './Scheduling.styles'
import { SchedulingProps } from './Scheduling.types'

function Scheduling({
  intl: { formatMessage },
  isSidebarOpened,
  setFormData,
  isFetching,
  licensee: { usaTimeFormat, usaDateFormat },
  onChangeFormValue,
  formData,
  validationErrors,
  setValidationErrors,
  isSchedulingCollapseOpened,
  setIsSchedulingCollapseOpened,
}: SchedulingProps & WrappedComponentProps) {
  const [monthRepeatType, setMonthRepeatType] = useState(0)

  const {
    [REGULAR_REPEAT_SCHEDULE]: {
      frequency = '',
      repeatBy = '',
      endDateAfterNumber = '',
      endDateAfterTerm = '',
      endDate = '',
      endDateOnDate = '',
    } = {},
  } = formData

  useEffect(() => {
    if (!isSidebarOpened) {
      setIsSchedulingCollapseOpened(false)
    }
  }, [isSidebarOpened])

  const formatListWithTranslation = (
    list: { label: string; value: string | number }[],
  ) =>
    list.map(({ value, label }) => ({
      value,
      label: formatMessage(messages[label]),
    }))

  const formattedAdditionalDueDateActions = formatListWithTranslation(
    ADDITIONAL_END_DELAY_ACTIONS,
  )

  const formattedAdditionRepeatActions = formatListWithTranslation(
    ADDITIONAL_REPEAT_ACTIONS,
  )

  const onChangeDueDate = (value: string) => {
    const isCustomValue = value === VALUE_CUSTOM
    const newValidationErrors = { ...validationErrors }

    delete newValidationErrors[FIELD_DUE_DATE]

    setValidationErrors(newValidationErrors)

    setFormData({
      ...formData,
      [FIELD_DUE_DATE_DATE]: isCustomValue ? new Date() : null,
      [FIELD_DUE_DATE_TIME]: isCustomValue ? new Date() : null,
      [FIELD_DUE_DATE]: value,
    })
  }

  const onChangeRecurrence = (value: string) => {
    const isCustomValue = value === VALUE_CUSTOM
    const isDefaultValue = value === VALUE_NEVER

    setFormData({
      ...formData,
      [REGULAR_REPEAT_SCHEDULE]: isDefaultValue
        ? {}
        : {
            ...(isCustomValue
              ? {
                  [FREQUENCY]: frequency || 1,
                  [REPEAT_BY]: repeatBy || VALUE_DAYS,
                }
              : {}),
            // END_DATE_AFTER_TERM and END_DATE_AFTER_NUMBER should be removed when NEVER option will be returned to the END_DATE
            [END_DATE]: endDate || VALUE_AFTER,
            ...(endDate === VALUE_ON
              ? {
                  [END_DATE_ON_DATE]: endDateOnDate || new Date(),
                }
              : {
                  [END_DATE_AFTER_TERM]: endDateAfterTerm || VALUE_OCCURRENCES,
                  [END_DATE_AFTER_NUMBER]: endDateAfterNumber || 3,
                }),
          },
      [REPEAT]: value,
    })

    const newValidationErrors = { ...validationErrors }

    delete newValidationErrors[REPEAT]
    delete newValidationErrors[REPEAT_BY]
    delete newValidationErrors[FREQUENCY]

    setValidationErrors(newValidationErrors)

    if (isDefaultValue) {
      delete newValidationErrors[END_DATE_AFTER_NUMBER]

      setValidationErrors(newValidationErrors)
    }
  }

  const onChangeRegularRepeat = (
    value: number | string | string[] | MaterialUiPickersDate,
    field: string,
  ) => {
    const newValidationErrors = { ...validationErrors }

    if (
      field === WEEK_ORDINARY_NUMBER ||
      field === DAY_OF_WEEK ||
      field === DAY_OF_MONTH
    ) {
      delete newValidationErrors[WEEK_ORDINARY_NUMBER]
      delete newValidationErrors[DAY_OF_WEEK]
      delete newValidationErrors[DAY_OF_MONTH]
    } else {
      delete newValidationErrors[field]
    }

    setValidationErrors(newValidationErrors)

    if (field === REPEAT_BY) {
      setFormData({
        ...formData,
        [REGULAR_REPEAT_SCHEDULE]: {
          frequency: +frequency,
          endDateAfterTerm,
          endDateAfterNumber: +endDateAfterNumber,
          endDate,
          [field as string]: value,
        },
      })

      return
    }

    if (field === END_DATE) {
      if (value === VALUE_NEVER) {
        setFormData({
          ...formData,
          [REGULAR_REPEAT_SCHEDULE]: {
            ...formData[REGULAR_REPEAT_SCHEDULE],
            [field]: value,
            [END_DATE_AFTER_TERM]: undefined,
            [END_DATE_AFTER_NUMBER]: undefined,
          },
        })
      } else if (value === VALUE_ON) {
        setFormData({
          ...formData,
          [REGULAR_REPEAT_SCHEDULE]: {
            ...formData[REGULAR_REPEAT_SCHEDULE],
            [field as string]: value,
            [END_DATE_ON_DATE]: endDateOnDate || new Date(),
            [END_DATE_AFTER_TERM]: undefined,
            [END_DATE_AFTER_NUMBER]: undefined,
          },
        })
      } else {
        setFormData({
          ...formData,
          [REGULAR_REPEAT_SCHEDULE]: {
            ...formData[REGULAR_REPEAT_SCHEDULE],
            [field as string]: value,
            [END_DATE_AFTER_TERM]: endDateAfterTerm || VALUE_OCCURRENCES,
            [END_DATE_AFTER_NUMBER]: endDateAfterNumber || 3,
            [END_DATE_ON_DATE]: undefined,
          },
        })
      }

      return
    }

    setFormData({
      ...formData,
      [REGULAR_REPEAT_SCHEDULE]: {
        ...formData[REGULAR_REPEAT_SCHEDULE],
        [field]: value,
      },
    })
  }

  const onClearMonthData = () => {
    setFormData({
      ...formData,
      [REGULAR_REPEAT_SCHEDULE]: {
        ...formData[REGULAR_REPEAT_SCHEDULE],
        [WEEK_ORDINARY_NUMBER]: undefined,
        [DAY_OF_WEEK]: undefined,
        [DAY_OF_MONTH]: undefined,
      },
    })
  }

  const formatDatePickerLabelFunc = (date: MaterialUiPickersDate) => {
    if (date?.toDateString() === new Date().toDateString()) {
      return formatMessage(messages.today)
    }

    return getFormattedDate(date, getDateFormat(usaDateFormat)).toString()
  }

  const timepickerLocale = ProfileHelper.getCurrentLocale()
  const isRepeatCustom = useMemo(() => formData[REPEAT] === VALUE_CUSTOM, [
    formData[REPEAT],
  ])
  const isRepeatNever = useMemo(() => formData[REPEAT] === VALUE_NEVER, [
    formData[REPEAT],
  ])

  return (
    <StyledModalBoxSection>
      <StyledRuleSettings>
        <StyledTitle
          expandable
          opened={isSchedulingCollapseOpened}
          onClick={() =>
            setIsSchedulingCollapseOpened(!isSchedulingCollapseOpened)
          }>
          <SchedulingIcon />
          {formatMessage(messages.scheduling)}
        </StyledTitle>
        <Collapse in={isSchedulingCollapseOpened}>
          <StyledCondition align="center" wrap="wrap" marginBottom={12}>
            <div>{`${formatMessage(messages.startDate)} ${formatMessage(
              messages.is,
            )}`}</div>
            <DatePickerContainer>
              <MuiPickersUtilsProvider
                utils={DateFnsUtils}
                locale={timepickerLocale}>
                <DatePicker
                  PopoverProps={{
                    PaperProps: {
                      component: DatePickerPaper,
                    },
                  }}
                  autoOk
                  disablePast
                  disabled={isFetching}
                  variant="inline"
                  value={formData[FIELD_START_DATE]}
                  onChange={value => onChangeFormValue(value, FIELD_START_DATE)}
                  labelFunc={formatDatePickerLabelFunc}
                />
              </MuiPickersUtilsProvider>
            </DatePickerContainer>
            <StyledName>{formatMessage(messages.at)}</StyledName>
            <MuiPickersUtilsProvider
              utils={DateFnsUtils}
              locale={timepickerLocale}>
              <StyledTimePicker
                PopoverProps={{
                  PaperProps: {
                    component: TimePickerPaper,
                  },
                }}
                keyboardIcon={<ClockIcon />}
                ampm={usaTimeFormat}
                disabled={isFetching}
                variant="inline"
                className={cn({
                  error: Object.prototype.hasOwnProperty.call(
                    validationErrors,
                    START_TIME,
                  ),
                })}
                onChange={date => {
                  const newValidationErrors = { ...validationErrors }

                  delete newValidationErrors[START_TIME]

                  setValidationErrors(newValidationErrors)
                  onChangeFormValue(date, START_TIME)
                }}
                value={formData.startTime}
              />
            </MuiPickersUtilsProvider>
          </StyledCondition>
          <StyledCondition align="center" wrap="wrap" marginBottom={12}>
            <StyledName>{formatMessage(messages.dueDate)}</StyledName>
            <SelectInput
              isValidationFailed={Object.prototype.hasOwnProperty.call(
                validationErrors,
                FIELD_DUE_DATE,
              )}
              disabled={isFetching}
              label={formatMessage(messages.dueDate)}
              options={formattedAdditionalDueDateActions}
              value={formData[FIELD_DUE_DATE]}
              onChange={(value: string) => {
                onChangeDueDate(value)
              }}
            />
          </StyledCondition>
          {formData[FIELD_DUE_DATE] === VALUE_CUSTOM && (
            <StyledCondition align="center" wrap="wrap" marginBottom={12}>
              <StyledName>{formatMessage(messages.dueDateIs)}</StyledName>
              <DatePickerContainer>
                <MuiPickersUtilsProvider
                  utils={DateFnsUtils}
                  locale={timepickerLocale}>
                  <DatePicker
                    PopoverProps={{
                      PaperProps: {
                        component: DatePickerPaper,
                      },
                    }}
                    required
                    autoOk
                    disablePast
                    disabled={isFetching}
                    variant="inline"
                    value={formData[FIELD_DUE_DATE_DATE]}
                    onChange={value =>
                      onChangeFormValue(value, FIELD_DUE_DATE_DATE)
                    }
                    labelFunc={formatDatePickerLabelFunc}
                  />
                </MuiPickersUtilsProvider>
              </DatePickerContainer>
              <StyledName>{formatMessage(messages.at)}</StyledName>
              <MuiPickersUtilsProvider
                utils={DateFnsUtils}
                locale={timepickerLocale}>
                <StyledTimePicker
                  PopoverProps={{
                    PaperProps: {
                      component: TimePickerPaper,
                    },
                  }}
                  className={cn({
                    error: Object.prototype.hasOwnProperty.call(
                      validationErrors,
                      FIELD_DUE_DATE_TIME,
                    ),
                  })}
                  variant="inline"
                  ampm={usaTimeFormat}
                  disabled={isFetching}
                  keyboardIcon={<ClockIcon />}
                  invalidLabel=""
                  onAccept={date =>
                    onChangeFormValue(date, FIELD_DUE_DATE_TIME)
                  }
                  onChange={date =>
                    onChangeFormValue(date, FIELD_DUE_DATE_TIME)
                  }
                  value={formData[FIELD_DUE_DATE_TIME]}
                />
              </MuiPickersUtilsProvider>
            </StyledCondition>
          )}
          <StyledCondition align="center" wrap="wrap" marginBottom={12}>
            <StyledName>{formatMessage(messages.repeat)}</StyledName>
            <SelectInput
              isValidationFailed={Object.prototype.hasOwnProperty.call(
                validationErrors,
                REPEAT,
              )}
              disabled={isFetching}
              options={formattedAdditionRepeatActions}
              value={formData[REPEAT]}
              onChange={(value: string) => onChangeRecurrence(value)}
            />
          </StyledCondition>
          {isRepeatCustom && (
            <>
              <StyledCondition align="center" wrap="wrap" marginBottom={12}>
                <StyledName>{formatMessage(messages.every)}</StyledName>
                <TextField
                  style={{ width: 55 }}
                  required
                  disabled={isFetching}
                  value={formData[REGULAR_REPEAT_SCHEDULE][FREQUENCY]}
                  error={Object.prototype.hasOwnProperty.call(
                    validationErrors,
                    FREQUENCY,
                  )}
                  onChange={({ target: { value } }) =>
                    onChangeRegularRepeat(+value, FREQUENCY)
                  }
                />
                <SelectInput
                  disabled={isFetching}
                  options={formatListWithTranslation(
                    FORMATTED_STOP_RECURRENCE_ACTIONS,
                  )}
                  value={formData[REGULAR_REPEAT_SCHEDULE][REPEAT_BY]}
                  onChange={(value: string) =>
                    onChangeRegularRepeat(value, REPEAT_BY)
                  }
                  isValidationFailed={Object.prototype.hasOwnProperty.call(
                    validationErrors,
                    REPEAT_BY,
                  )}
                />
              </StyledCondition>
              {formData[REGULAR_REPEAT_SCHEDULE][REPEAT_BY] === VALUE_WEEKS && (
                <StyledCondition align="center" wrap="wrap" marginBottom={12}>
                  <DaysContainer
                    error={Object.prototype.hasOwnProperty.call(
                      validationErrors,
                      DAYS_OF_WEEK,
                    )}>
                    {DAYS_OF_WEEK_SHORTENED_OPTIONS.map(({ label, value }) => (
                      <Day
                        justify="center"
                        key={label}
                        noBorder={value === 'Sun'}
                        onClick={() => {
                          const oldValue =
                            formData[REGULAR_REPEAT_SCHEDULE][DAYS_OF_WEEK] ||
                            []
                          let newValue = []

                          if (oldValue.includes(value)) {
                            newValue = oldValue.filter(
                              oldVal => oldVal !== value,
                            )
                          } else {
                            newValue = [...oldValue, value]
                          }

                          onChangeRegularRepeat(newValue, DAYS_OF_WEEK)
                        }}
                        selected={formData[REGULAR_REPEAT_SCHEDULE][
                          DAYS_OF_WEEK
                        ]?.includes(value)}>
                        {formatMessage(messages[label])}
                      </Day>
                    ))}
                  </DaysContainer>
                </StyledCondition>
              )}
              {formData[REGULAR_REPEAT_SCHEDULE][REPEAT_BY] ===
                VALUE_MONTHS && (
                <>
                  <StyledCondition align="center" wrap="wrap" marginBottom={12}>
                    <RadioContainer
                      align="center"
                      onClick={() => {
                        setMonthRepeatType(0)
                        onClearMonthData()
                      }}>
                      <Radio
                        checked={monthRepeatType === 0}
                        onChange={event => {
                          event.preventDefault()
                          setMonthRepeatType(0)
                          onClearMonthData()
                        }}
                        color={PURPLE}
                      />
                      {formatMessage(messages.each)}
                    </RadioContainer>
                    <SelectInput
                      multiple
                      disabled={isFetching || monthRepeatType === 1}
                      options={DAYS_OF_MONTH}
                      value={formData[REGULAR_REPEAT_SCHEDULE][DAY_OF_MONTH]}
                      onChange={(value: string) =>
                        onChangeRegularRepeat(value, DAY_OF_MONTH)
                      }
                      isValidationFailed={Object.prototype.hasOwnProperty.call(
                        validationErrors,
                        DAY_OF_MONTH,
                      )}
                    />
                  </StyledCondition>
                  <StyledCondition align="center" wrap="wrap" marginBottom={12}>
                    <RadioContainer
                      align="center"
                      onClick={() => {
                        setMonthRepeatType(1)
                        onClearMonthData()
                      }}>
                      <Radio
                        checked={monthRepeatType === 1}
                        onChange={event => {
                          event.preventDefault()
                          setMonthRepeatType(1)
                          onClearMonthData()
                        }}
                        color={PURPLE}
                      />
                      {formatMessage(messages.onThe)}
                    </RadioContainer>
                    <SelectInput
                      disabled={isFetching || monthRepeatType === 0}
                      options={formatListWithTranslation(WEEK_NUMBERS)}
                      value={
                        formData[REGULAR_REPEAT_SCHEDULE][WEEK_ORDINARY_NUMBER]
                      }
                      onChange={(value: string) => {
                        onChangeRegularRepeat(value, WEEK_ORDINARY_NUMBER)
                      }}
                      isValidationFailed={Object.prototype.hasOwnProperty.call(
                        validationErrors,
                        WEEK_ORDINARY_NUMBER,
                      )}
                    />
                    <SelectInput
                      disabled={isFetching || monthRepeatType === 0}
                      options={formatListWithTranslation(DAYS_OF_WEEK_OPTIONS)}
                      value={formData[REGULAR_REPEAT_SCHEDULE][DAY_OF_WEEK]}
                      onChange={(value: string) => {
                        onChangeRegularRepeat(value, DAY_OF_WEEK)
                      }}
                      isValidationFailed={Object.prototype.hasOwnProperty.call(
                        validationErrors,
                        DAY_OF_WEEK,
                      )}
                    />
                  </StyledCondition>
                </>
              )}
            </>
          )}
          {!isRepeatNever && (
            <>
              <StyledName marginBottom={12}>
                {formatMessage(messages.ends)}
              </StyledName>
              <StyledCondition align="center" wrap="wrap" marginBottom={12}>
                <RadioContainer
                  align="center"
                  onClick={() => onChangeRegularRepeat(VALUE_ON, END_DATE)}>
                  <Radio
                    checked={
                      formData[REGULAR_REPEAT_SCHEDULE][END_DATE] === VALUE_ON
                    }
                    onChange={event => {
                      event.preventDefault()
                      onChangeRegularRepeat(VALUE_ON, END_DATE)
                    }}
                    color={PURPLE}
                  />
                  {formatMessage(messages.on)}
                </RadioContainer>
                <DatePickerContainer>
                  <MuiPickersUtilsProvider
                    utils={DateFnsUtils}
                    locale={timepickerLocale}>
                    <DatePicker
                      PopoverProps={{
                        PaperProps: {
                          component: DatePickerPaper,
                        },
                      }}
                      required
                      autoOk
                      disablePast
                      disabled={
                        isFetching ||
                        formData[REGULAR_REPEAT_SCHEDULE][END_DATE] !== VALUE_ON
                      }
                      variant="inline"
                      value={
                        formData[REGULAR_REPEAT_SCHEDULE][END_DATE_ON_DATE]
                      }
                      onChange={value =>
                        onChangeRegularRepeat(value, END_DATE_ON_DATE)
                      }
                      labelFunc={formatDatePickerLabelFunc}
                    />
                  </MuiPickersUtilsProvider>
                </DatePickerContainer>
              </StyledCondition>
              <StyledCondition align="center" wrap="wrap" marginBottom={12}>
                <RadioContainer
                  align="center"
                  onClick={() => onChangeRegularRepeat(VALUE_AFTER, END_DATE)}>
                  <Radio
                    checked={
                      formData[REGULAR_REPEAT_SCHEDULE][END_DATE] ===
                      VALUE_AFTER
                    }
                    onChange={event => {
                      event.preventDefault()
                      onChangeRegularRepeat(VALUE_AFTER, END_DATE)
                    }}
                    color={PURPLE}
                  />
                  {formatMessage(messages.after)}
                </RadioContainer>
                <TextFieldContainer>
                  <TextField
                    required
                    className={cn({
                      error: Object.prototype.hasOwnProperty.call(
                        validationErrors,
                        END_DATE_AFTER_NUMBER,
                      ),
                    })}
                    disabled={
                      isFetching ||
                      formData[REGULAR_REPEAT_SCHEDULE][END_DATE] === VALUE_ON
                    }
                    value={
                      formData[REGULAR_REPEAT_SCHEDULE][END_DATE_AFTER_NUMBER]
                    }
                    onChange={({ target: { value } }) => {
                      onChangeRegularRepeat(+value, END_DATE_AFTER_NUMBER)
                    }}
                  />
                </TextFieldContainer>
                {formatMessage(messages.occurrences).toLowerCase()}
              </StyledCondition>
            </>
          )}
        </Collapse>
      </StyledRuleSettings>
    </StyledModalBoxSection>
  )
}

export default connect(
  (state: { getIn: (path: Array<string>) => Licensee | boolean }) => ({
    licensee: state.getIn(['profile', 'profile', 'licensee']) as Licensee,
    isFetching: state.getIn(['myJobs', 'isFetching']) as boolean,
  }),
  {
    setFormData,
  },
)(injectIntl(Scheduling))
